import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Card, CardContent, TextField, Stack, Grid, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Switch,
  FormControlLabel, CircularProgress, Alert, Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import SettingsIcon from '@mui/icons-material/Settings';
import SendIcon from '@mui/icons-material/Send';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DescriptionIcon from '@mui/icons-material/Description';
import { toast } from 'react-toastify';
import { getInviteSettings, updateInviteSettings, sendInvite } from '../../api/endpoints/invite';
import { uploadFile } from '../../api/endpoints/media';

const initialSender = { name: '', email: '' };

export default function InviteAstrologer() {
  const [senders, setSenders] = useState([{ ...initialSender }]);
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [recipients, setRecipients] = useState(['']);
  const [documents, setDocuments] = useState([]);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const [manageOpen, setManageOpen] = useState(false);
  const [settings, setSettings] = useState(null);
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);

  const [editSenderName, setEditSenderName] = useState('');
  const [editSenderEmail, setEditSenderEmail] = useState('');
  const [editSubject, setEditSubject] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [editDocs, setEditDocs] = useState([]);
  const [editSmtpHost, setEditSmtpHost] = useState('');
  const [editSmtpPort, setEditSmtpPort] = useState(587);
  const [editSmtpUser, setEditSmtpUser] = useState('');
  const [editSmtpPass, setEditSmtpPass] = useState('');
  const [editSmtpSecure, setEditSmtpSecure] = useState(false);

  const handleSenderChange = (i, field, val) => {
    const copy = [...senders];
    copy[i][field] = val;
    setSenders(copy);
  };
  const addSender = () => setSenders([...senders, { ...initialSender }]);
  const removeSender = (i) => { if (senders.length > 1) setSenders(senders.filter((_, idx) => idx !== i)); };

  const handleRecipientChange = (i, val) => {
    const copy = [...recipients];
    copy[i] = val;
    setRecipients(copy);
  };
  const addRecipient = () => setRecipients([...recipients, '']);
  const removeRecipient = (i) => { if (recipients.length > 1) setRecipients(recipients.filter((_, idx) => idx !== i)); };

  const handleDocUpload = async (e) => {
    const files = e.target.files;
    if (!files?.length) return;
    for (const file of files) {
      try {
        const res = await uploadFile(file);
        const url = res.data?.data?.url || res.data?.url;
        if (url) setDocuments((prev) => [...prev, url]);
      } catch { toast.error(`Failed to upload ${file.name}`); }
    }
    e.target.value = '';
  };
  const removeDoc = (idx) => setDocuments(documents.filter((_, i) => i !== idx));

  const handleManageDocUpload = async (e) => {
    const files = e.target.files;
    if (!files?.length) return;
    for (const file of files) {
      try {
        const res = await uploadFile(file);
        const url = res.data?.data?.url || res.data?.url;
        if (url) setEditDocs((prev) => [...prev, url]);
      } catch { toast.error(`Failed to upload ${file.name}`); }
    }
    e.target.value = '';
  };
  const removeEditDoc = (idx) => setEditDocs(editDocs.filter((_, i) => i !== idx));

  const openManage = async () => {
    setManageOpen(true);
    setLoadingSettings(true);
    try {
      const res = await getInviteSettings();
      const data = res.data?.data || {};
      setSettings(data);
      setEditSenderName(data.senderName || '');
      setEditSenderEmail(data.senderEmail || '');
      setEditSubject(data.defaultSubject || '');
      setEditDesc(data.defaultDescription || '');
      setEditDocs(data.documents || []);
      setEditSmtpHost(data.smtpHost || '');
      setEditSmtpPort(data.smtpPort || 587);
      setEditSmtpUser(data.smtpUser || '');
      setEditSmtpPass(data.smtpPass || '');
      setEditSmtpSecure(data.smtpSecure || false);
    } catch { toast.error('Failed to load settings'); }
    finally { setLoadingSettings(false); }
  };

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      const payload = {
        senderName: editSenderName,
        senderEmail: editSenderEmail,
        defaultSubject: editSubject,
        defaultDescription: editDesc,
        documents: editDocs,
        smtpHost: editSmtpHost,
        smtpPort: editSmtpPort,
        smtpUser: editSmtpUser,
        smtpPass: editSmtpPass,
        smtpSecure: editSmtpSecure,
      };
      const res = await updateInviteSettings(payload);
      setSettings(res.data?.data || payload);
      toast.success('Settings saved');
      setManageOpen(false);
    } catch { toast.error('Failed to save settings'); }
    finally { setSavingSettings(false); }
  };

  const handleSubmit = async () => {
    const validRecipients = recipients.filter((r) => r.trim());
    if (!validRecipients.length) { toast.error('Add at least one recipient'); return; }
    const validSenders = senders.filter((s) => s.name.trim() || s.email.trim());
    setSending(true);
    setResult(null);
    try {
      const res = await sendInvite({
        senderName: validSenders[0]?.name || undefined,
        senderEmail: validSenders[0]?.email || undefined,
        subject: subject || undefined,
        description: description || undefined,
        recipients: validRecipients,
        documents,
      });
      const data = res.data;
      setResult(data);
      toast.success(`Sent ${data.totalSent} / ${validRecipients.length} invites`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send invites');
    } finally { setSending(false); }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Invite An Astrologer</Typography>
          <Typography variant="body2" color="text.secondary">Send invitation emails to astrologers</Typography>
        </Box>
        <Button variant="outlined" startIcon={<SettingsIcon />} onClick={openManage}>
          Manage
        </Button>
      </Stack>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} mb={2}>Sender Details</Typography>
          {senders.map((s, i) => (
            <Stack key={i} direction="row" spacing={1.5} alignItems="center" mb={1.5}>
              <TextField label="Name" size="small" fullWidth value={s.name} onChange={(e) => handleSenderChange(i, 'name', e.target.value)} />
              <TextField label="Email" size="small" fullWidth value={s.email} onChange={(e) => handleSenderChange(i, 'email', e.target.value)} />
              {senders.length > 1 && (
                <IconButton color="error" onClick={() => removeSender(i)}><RemoveCircleIcon /></IconButton>
              )}
            </Stack>
          ))}
          <Button size="small" startIcon={<AddIcon />} onClick={addSender}>Add Sender</Button>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack spacing={2}>
            <TextField label="Subject" fullWidth value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Leave blank to use default" />
            <TextField label="Custom Description" fullWidth multiline rows={4} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Leave blank to use default description" />
          </Stack>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} mb={2}>Recipients</Typography>
          {recipients.map((r, i) => (
            <Stack key={i} direction="row" spacing={1.5} alignItems="center" mb={1.5}>
              <TextField label="Email address" size="small" fullWidth value={r} onChange={(e) => handleRecipientChange(i, e.target.value)} />
              {recipients.length > 1 && (
                <IconButton color="error" onClick={() => removeRecipient(i)}><RemoveCircleIcon /></IconButton>
              )}
            </Stack>
          ))}
          <Button size="small" startIcon={<AddIcon />} onClick={addRecipient}>Add Recipient</Button>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} mb={2}>Documents</Typography>
          <Button variant="outlined" component="label" startIcon={<CloudUploadIcon />}>
            Upload Documents
            <input type="file" hidden multiple onChange={handleDocUpload} />
          </Button>
          {documents.length > 0 && (
            <Stack direction="row" spacing={1} flexWrap="wrap" mt={2}>
              {documents.map((doc, i) => (
                <Chip key={i} icon={<DescriptionIcon />} label={doc.split('/').pop()} onDelete={() => removeDoc(i)} size="small" />
              ))}
            </Stack>
          )}
        </CardContent>
      </Card>

      <Button variant="contained" size="large" startIcon={<SendIcon />} onClick={handleSubmit} disabled={sending} fullWidth>
        {sending ? 'Sending...' : 'Send Invites'}
      </Button>

      {result && (
        <Alert severity={result.totalFailed > 0 ? 'warning' : 'success'} sx={{ mt: 2 }}>
          Sent: {result.totalSent} / Failed: {result.totalFailed}
          {result.results?.filter((r) => !r.success).map((r, i) => (
            <Box key={i} component="div" sx={{ fontSize: 12, mt: 0.5 }}>{r.email}: {r.error}</Box>
          ))}
        </Alert>
      )}

      <Dialog open={manageOpen} onClose={() => setManageOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Manage Default Invite Settings</DialogTitle>
        <DialogContent dividers>
          {loadingSettings ? (
            <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>
          ) : (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Typography variant="subtitle2" fontWeight={600}>Default Sender</Typography>
              <Stack direction="row" spacing={1.5}>
                <TextField label="Sender Name" size="small" fullWidth value={editSenderName} onChange={(e) => setEditSenderName(e.target.value)} />
                <TextField label="Sender Email" size="small" fullWidth value={editSenderEmail} onChange={(e) => setEditSenderEmail(e.target.value)} />
              </Stack>
              <Divider />
              <TextField label="Default Subject" fullWidth value={editSubject} onChange={(e) => setEditSubject(e.target.value)} />
              <TextField label="Default Description" fullWidth multiline rows={3} value={editDesc} onChange={(e) => setEditDesc(e.target.value)} />
              <Divider />
              <Typography variant="subtitle2" fontWeight={600}>Default Documents</Typography>
              <Button variant="outlined" component="label" size="small" startIcon={<CloudUploadIcon />}>
                Upload
                <input type="file" hidden multiple onChange={handleManageDocUpload} />
              </Button>
              {editDocs.length > 0 && (
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {editDocs.map((doc, i) => (
                    <Chip key={i} icon={<DescriptionIcon />} label={doc.split('/').pop()} onDelete={() => removeEditDoc(i)} size="small" />
                  ))}
                </Stack>
              )}
              <Divider />
              <Typography variant="subtitle2" fontWeight={600}>SMTP Configuration</Typography>
              <Grid container spacing={1.5}>
                <Grid item xs={6}><TextField label="SMTP Host" size="small" fullWidth value={editSmtpHost} onChange={(e) => setEditSmtpHost(e.target.value)} /></Grid>
                <Grid item xs={3}><TextField label="Port" size="small" type="number" fullWidth value={editSmtpPort} onChange={(e) => setEditSmtpPort(Number(e.target.value))} /></Grid>
                <Grid item xs={3}>
                  <FormControlLabel control={<Switch checked={editSmtpSecure} onChange={(e) => setEditSmtpSecure(e.target.checked)} />} label="Secure" sx={{ mt: 1 }} />
                </Grid>
                <Grid item xs={6}><TextField label="SMTP User" size="small" fullWidth value={editSmtpUser} onChange={(e) => setEditSmtpUser(e.target.value)} /></Grid>
                <Grid item xs={6}><TextField label="SMTP Password" size="small" type="password" fullWidth value={editSmtpPass} onChange={(e) => setEditSmtpPass(e.target.value)} /></Grid>
              </Grid>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManageOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveSettings} disabled={savingSettings || loadingSettings}>
            {savingSettings ? 'Saving...' : 'Save Settings'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
